import { useNavigation } from "@repo/core/hooks/useNavigation";
import { useCallback, useEffect, useRef, useState } from "react";

import useToast from "@repo/core/components/core/Toast";
import { ROUTES } from "@repo/core/routes";
import {
  getNextRouteAfterProductSelection,
  hasSelectedDifferentProduct
} from "../../hooks/usePreProposal/navigation";
import { usePreProposalStore } from "../../store/preProposalStore";
import useAuthStore from "@repo/core/stores/auth";
import { useMapperProducts } from "./useMapperProducts";
import {
  getProductsScreenOutcome,
  NO_AVAILABLE_PRODUCTS_TOAST
} from "./utils";

export const SelectProductController = () => {
  const { back, push, replace } = useNavigation();
  const toast = useToast();
  const { user } = useAuthStore();

  const { proposalData, setProposalData, resetPreProposalStore } =
    usePreProposalStore();

  const { products, isFetchingProducts } = useMapperProducts();

  const [selectedProductId, setSelectedProductId] = useState<null | string>(
    null
  );
  const hasAutoNavigated = useRef(false);

  const navigateAfterProductSelection = useCallback(
    (productId: string, useReplace = false) => {
      if (hasSelectedDifferentProduct(proposalData?.strProduto, productId)) {
        resetPreProposalStore();
      }

      setProposalData({ strProduto: productId }, { skipAlterarSync: true });

      const nextStep = getNextRouteAfterProductSelection(user?.tokenData);

      if (nextStep.strPontoAtendimento) {
        setProposalData(
          { strPontoAtendimento: nextStep.strPontoAtendimento },
          { skipAlterarSync: true }
        );
      }

      const navigate = useReplace ? replace : push;
      navigate(nextStep.route);
    },
    [
      proposalData?.strProduto,
      push,
      replace,
      resetPreProposalStore,
      setProposalData,
      user?.tokenData
    ]
  );

  const handleBack = () => {
    resetPreProposalStore();
    back();
  };

  const handleSelectProduct = (id: string) => {
    setSelectedProductId((prev) => (prev === id ? null : id));
  };

  const handleOnSubmitForm = () => {
    if (selectedProductId) {
      navigateAfterProductSelection(selectedProductId);
    } else {
      toast.error("Produto não selecionado", "Por favor, selecione um produto");
    }
  };

  useEffect(() => {
    if (hasAutoNavigated.current) return;

    const outcome = getProductsScreenOutcome(
      isFetchingProducts,
      products.length
    );

    if (outcome === "autoSelect") {
      hasAutoNavigated.current = true;
      navigateAfterProductSelection(products[0].id, true);
      return;
    }

    if (outcome === "empty") {
      hasAutoNavigated.current = true;
      toast.error(
        NO_AVAILABLE_PRODUCTS_TOAST.title,
        NO_AVAILABLE_PRODUCTS_TOAST.description
      );
      resetPreProposalStore();
      replace(ROUTES.home);
    }
  }, [
    isFetchingProducts,
    navigateAfterProductSelection,
    products,
    replace,
    resetPreProposalStore,
    toast
  ]);

  return {
    back,
    handleOnSubmitForm,
    selectedProductId,
    products,
    handleBack,
    handleSelectProduct,
    isFetchingProducts
  };
};
